import { useEffect, useRef, useState } from "react";
import { loops, textures } from "@/config/media";
import { useCapabilities } from "@/lib/device";
import useIosSafeVideo from "@/lib/useIosSafeVideo";
import { isHeroDone, subscribe } from "@/lib/livingRegistry";

// Almost-subliminal atmosphere layer for text-heavy sections. Full-bleed,
// object-cover, 6–9% opacity, blur(8px). Ground colour overlaid so type
// contrast is unchanged. Poster shows first, the loop swaps in on capable
// devices once the section is in view and the hero has finished.
export default function AmbientBand({ loop, texture, opacity = 0.08 }) {
  const caps = useCapabilities();
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  useIosSafeVideo(videoRef);
  const [inView, setInView] = useState(false);
  const [heroReady, setHeroReady] = useState(isHeroDone());
  const runVideo = caps.ready && caps.webgl && !caps.reduced && heroReady;

  // hero-ready gate — subscribes to the shared registry, which also arms
  // a 3.5s fallback so this can never stall indefinitely.
  useEffect(() => {
    if (heroReady) return;
    return subscribe(() => {
      if (isHeroDone()) setHeroReady(true);
    });
  }, [heroReady]);

  // Observe the stable wrapper (never swaps), not the media element.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Control play only when both in view and the run-gate is open.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView && runVideo) v.play().catch(() => {});
    else v.pause();
  }, [inView, runVideo]);

  const t = textures[texture];
  return (
    <div
      ref={wrapRef}
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
      data-testid={`ambient-${loop}`}
    >
      {/* Poster always paints first — the film fades in on top once armed. */}
      {t && (
        <img
          src={`${t.base}-1600.jpg`}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: opacity + 0.02, filter: "blur(10px) saturate(0.85)" }}
        />
      )}
      {runVideo && (
        <video
          ref={videoRef}
          src={inView ? loops[loop] : undefined}
          muted
          autoPlay
          loop
          playsInline
          preload="none"
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out"
          style={{ opacity, filter: "blur(8px) saturate(0.85)" }}
        />
      )}
      {/* Ground wash on top so type contrast doesn't shift */}
      <div className="absolute inset-0 bg-ground/72" />
    </div>
  );
}
